import React from "react";
import PageMeta from "@insightfulscience/smart-react/PageMeta";
import { useLocale, WithLocale } from "@insightfulscience/i18";
import Html from "@insightfulscience/smart-react/Html";
import {
	PostElementTitleH4,
	PostElementParagraph,
	PostElementListItem,
	PostElementOl,
	PostElementUl,
} from "@insightfulscience/atomic-react/PostElement";
import GuidePageTemplate from "@insightfulscience/atomic-react/GuidePageTemplate";
import GuidePageTemplateMain from "@insightfulscience/atomic-react/GuidePageTemplate/Main";
import Hero from "@insightfulscience/atomic-react/Hero";
import HeroTitle from "@insightfulscience/atomic-react/Hero/Title";
import pipe from "@insightfulscience/shared-utils/fn/pipe";
import SiteLayout from "@src/SiteLayout";
import resources from "../locales/privacy-policy-page";

const PrivacyPolicy = () => {
	const [t] = useLocale("privacy-policy-page");
	return (
		<SiteLayout hero>
			<PageMeta title={t("PAGE_TITLE")} description={t("META_DESCRIPTION")}>
				<PageMeta.OGP
					title={t("PAGE_TITLE")}
					description={t("META_DESCRIPTION")}
					locale={t("META_LOCALE")}
					type={t("META_TYPE")}
					url={t("META_URL")}
					image={t("META_IMAGE")}
					imageSecureUrl={t("META_IMAGE_SECURE")}
					siteName={t("META_SITE_NAME")}
					articlePublisher={t("META_ARTICLE_PUBLISHER")}
					isImageBaseUrl
				/>
				<PageMeta.Twitter
					title={t("PAGE_TITLE")}
					description={t("META_DESCRIPTION")}
					site={t("META_TWITTER_SITE")}
					card={t("META_TWITTER_CARD")}
					image={t("META_TWITTER_IMAGE")}
					creator={t("META_TWITTER_CREATOR")}
					isImageBaseUrl
				/>
				<PageMeta.Canonical href={t("META_CANONICAL")} />
				<PageMeta.Author content={t("META_AUTHOR")} />
			</PageMeta>
			<Hero>
				<HeroTitle>{t("TITLE")}</HeroTitle>
			</Hero>
			<GuidePageTemplate sidebar={false}>
				<GuidePageTemplateMain sidebar={false}>
					<PostElementParagraph>{t("EFFECTIVE_TODO_DATE")}</PostElementParagraph>

					<PostElementTitleH4>{t("SCOPE_TITLE")}</PostElementTitleH4>
					<PostElementParagraph>{t("SCOPE_PARAGRAPH_1")}</PostElementParagraph>
					<PostElementParagraph>{t("SCOPE_PARAGRAPH_2")}</PostElementParagraph>
					<PostElementParagraph>{t("SCOPE_PARAGRAPH_3")}</PostElementParagraph>
					<PostElementParagraph>{t("SCOPE_PARAGRAPH_4")}</PostElementParagraph>

					<PostElementTitleH4>{t("PERSONAL_INFO_TITLE")}</PostElementTitleH4>
					<PostElementParagraph>{t("PERSONAL_INFO_PARAGRAPH_1")}</PostElementParagraph>
					<PostElementParagraph>
						<Html Tag="span">
							{t("PERSONAL_INFO_PARAGRAPH_2", { snapgene: "https://www.snapgene.com" })}
						</Html>
					</PostElementParagraph>
					<PostElementParagraph>{t("PERSONAL_INFO_PARAGRAPH_3")}</PostElementParagraph>
					<PostElementOl>
						<PostElementListItem>{t("PERSONAL_INFO_LIST_ITEM_1")}</PostElementListItem>
						<PostElementListItem>{t("PERSONAL_INFO_LIST_ITEM_2")}</PostElementListItem>
					</PostElementOl>
					<PostElementParagraph>{t("PERSONAL_INFO_PARAGRAPH_4")}</PostElementParagraph>

					<PostElementTitleH4>{t("HOW_TO_USE_TITLE")}</PostElementTitleH4>
					<PostElementParagraph>{t("HOW_TO_USE_PARAGRAPH_1")}</PostElementParagraph>
					<PostElementOl>
						<PostElementListItem>{t("HOW_TO_USE_LIST_ITEM_1")}</PostElementListItem>
						<PostElementListItem>{t("HOW_TO_USE_LIST_ITEM_2")}</PostElementListItem>
						<PostElementListItem>{t("HOW_TO_USE_LIST_ITEM_3")}</PostElementListItem>
						<PostElementListItem>{t("HOW_TO_USE_LIST_ITEM_4")}</PostElementListItem>
						<PostElementListItem>{t("HOW_TO_USE_LIST_ITEM_5")}</PostElementListItem>
						<PostElementListItem>{t("HOW_TO_USE_LIST_ITEM_6")}</PostElementListItem>
						<PostElementListItem>{t("HOW_TO_USE_LIST_ITEM_7")}</PostElementListItem>
						<PostElementListItem>{t("HOW_TO_USE_LIST_ITEM_8")}</PostElementListItem>
					</PostElementOl>

					<PostElementTitleH4>{t("HOW_TO_SHARE_TITLE")}</PostElementTitleH4>
					<PostElementParagraph>{t("HOW_TO_SHARE_PPARAGRAPH_1")}</PostElementParagraph>
					<PostElementParagraph>{t("HOW_TO_SHARE_PPARAGRAPH_2")}</PostElementParagraph>
					<PostElementParagraph>{t("HOW_TO_SHARE_PPARAGRAPH_3")}</PostElementParagraph>
					<PostElementParagraph>{t("HOW_TO_SHARE_PPARAGRAPH_4")}</PostElementParagraph>
					<PostElementOl>
						<PostElementListItem>{t("HOW_TO_SHARE_LIST_ITEM_1")}</PostElementListItem>
						<PostElementListItem>{t("HOW_TO_SHARE_LIST_ITEM_2")}</PostElementListItem>
						<PostElementListItem>{t("HOW_TO_SHARE_LIST_ITEM_3")}</PostElementListItem>
						<PostElementListItem>{t("HOW_TO_SHARE_LIST_ITEM_4")}</PostElementListItem>
					</PostElementOl>
					<PostElementParagraph>{t("HOW_TO_SHARE_PPARAGRAPH_5")}</PostElementParagraph>
					<PostElementParagraph>{t("HOW_TO_SHARE_PPARAGRAPH_6")}</PostElementParagraph>

					<PostElementTitleH4>{t("COOKIES_AND_SECURITY_TITLE")}</PostElementTitleH4>
					<PostElementParagraph>{t("COOKIES_AND_SECURITY_PARAGRAPH")}</PostElementParagraph>

					<PostElementTitleH4>{t("COMPLIANCE_TITLE")}</PostElementTitleH4>
					<PostElementParagraph>{t("COMPLIANCE_PARAGRAPH_1")}</PostElementParagraph>
					<PostElementUl>
						<PostElementListItem>{t("COMPLIANCE_LIST_ITEM_1")}</PostElementListItem>
						<PostElementListItem>{t("COMPLIANCE_LIST_ITEM_2")}</PostElementListItem>
						<PostElementListItem>{t("COMPLIANCE_LIST_ITEM_3")}</PostElementListItem>
						<PostElementListItem>{t("COMPLIANCE_LIST_ITEM_4")}</PostElementListItem>
						<PostElementListItem>{t("COMPLIANCE_LIST_ITEM_5")}</PostElementListItem>
					</PostElementUl>
					<PostElementParagraph>
						<Html Tag="span">
							{t("COMPLIANCE_PARAGRAPH_2", { infoSnapgene: "mailto:info@snapgene.com" })}
						</Html>
					</PostElementParagraph>

					<PostElementTitleH4>{t("LOCATION_DATA_TITLE")}</PostElementTitleH4>
					<PostElementParagraph>
						<Html Tag="span">
							{t("LOCATION_DATA_PARAGRAPH_1", { liteLocation: "https://lite.ip2location.com" })}
						</Html>
					</PostElementParagraph>
					<PostElementParagraph>{t("LOCATION_DATA_PARAGRAPH_2")}</PostElementParagraph>

					<PostElementTitleH4>{t("RECAPTCHA_TITLE")}</PostElementTitleH4>
					<PostElementParagraph>
						<Html Tag="span">
							{t("RECAPTCHA_PARAGRAPH", {
								privacyPolicy: "https://policies.google.com/privacy",
								termsOfService: "https://policies.google.com/terms",
							})}
						</Html>
					</PostElementParagraph>
				</GuidePageTemplateMain>
			</GuidePageTemplate>
		</SiteLayout>
	);
};

export default pipe(PrivacyPolicy, WithLocale({ resources }));
